"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.parseIsAnyOfValues = parseIsAnyOfValues;
exports.buildStringOperatorClause = buildStringOperatorClause;
exports.parseNumberValue = parseNumberValue;
exports.buildNumberOperatorClause = buildNumberOperatorClause;
exports.parseDateValue = parseDateValue;
exports.buildDateOperatorClause = buildDateOperatorClause;
exports.buildBooleanOperatorClause = buildBooleanOperatorClause;
exports.buildRelationOperatorClause = buildRelationOperatorClause;
function parseIsAnyOfValues(value) {
    if (value === undefined || value === '') {
        throw new Error('isAnyOf filter requires a value.');
    }
    try {
        const parsed = JSON.parse(value);
        if (Array.isArray(parsed)) {
            const values = parsed
                .filter((item) => {
                const t = typeof item;
                return t === 'string' || t === 'number' || t === 'boolean';
            })
                .map(String);
            if (values.length === 0) {
                throw new Error('isAnyOf filter contains no valid string values.');
            }
            return values;
        }
        else if (typeof parsed === 'string' || typeof parsed === 'number' || typeof parsed === 'boolean') {
            return [String(parsed)];
        }
        throw new Error(`isAnyOf filter received unsupported JSON type: ${typeof parsed}.`);
    }
    catch (e) {
        if (e instanceof SyntaxError) {
            const values = value
                .split(',')
                .map((v) => v.trim())
                .filter(Boolean);
            if (values.length === 0) {
                throw new Error('isAnyOf filter value produced no valid entries.');
            }
            return values;
        }
        throw e;
    }
}
function buildStringOperatorClause(column, operator, value, options) {
    switch (operator) {
        case 'contains':
            return { [column]: { contains: value, mode: 'insensitive' } };
        case 'doesNotContain':
            return { NOT: { [column]: { contains: value, mode: 'insensitive' } } };
        case 'equals':
            return { [column]: { equals: value } };
        case 'doesNotEqual':
            return { [column]: { not: { equals: value } } };
        case 'startsWith':
            return { [column]: { startsWith: value, mode: 'insensitive' } };
        case 'endsWith':
            return { [column]: { endsWith: value, mode: 'insensitive' } };
        case 'isEmpty':
            if (options?.nonNullable) {
                return { [column]: { equals: '' } };
            }
            return { [column]: { in: [null, ''] } };
        case 'isNotEmpty':
            if (options?.nonNullable) {
                return { [column]: { not: { equals: '' } } };
            }
            return { [column]: { not: { in: [null, ''] } } };
        case 'is':
            return { [column]: { equals: value } };
        case 'not':
            return { [column]: { not: { equals: value } } };
        case 'isAnyOf': {
            const values = parseIsAnyOfValues(value);
            return { [column]: { in: values } };
        }
        case 'isNoneOf': {
            const values = parseIsAnyOfValues(value);
            return { [column]: { not: { in: values } } };
        }
        default:
            return null;
    }
}
function parseNumberValue(value) {
    if (value === undefined || value === '') {
        throw new Error('Number filter requires a value.');
    }
    const n = Number(value);
    if (!Number.isFinite(n)) {
        throw new Error(`Number filter received non-numeric value: ${value}`);
    }
    return n;
}
function parseNumberIsAnyOfValues(value) {
    const raw = parseIsAnyOfValues(value);
    return raw.map(parseNumberValue);
}
function buildNumberOperatorClause(column, operator, value) {
    switch (operator) {
        case 'equals':
        case 'is':
            return { [column]: { equals: parseNumberValue(value) } };
        case 'doesNotEqual':
        case 'not':
            return { [column]: { not: { equals: parseNumberValue(value) } } };
        case 'greaterThan':
            return { [column]: { gt: parseNumberValue(value) } };
        case 'greaterThanOrEqual':
            return { [column]: { gte: parseNumberValue(value) } };
        case 'lessThan':
            return { [column]: { lt: parseNumberValue(value) } };
        case 'lessThanOrEqual':
            return { [column]: { lte: parseNumberValue(value) } };
        case 'isEmpty':
            return { [column]: null };
        case 'isNotEmpty':
            return { [column]: { not: null } };
        case 'isAnyOf':
            return { [column]: { in: parseNumberIsAnyOfValues(value) } };
        case 'isNoneOf':
            return { [column]: { not: { in: parseNumberIsAnyOfValues(value) } } };
        default:
            return null;
    }
}
function parseDateValue(value) {
    if (value === undefined || value === '') {
        throw new Error('Date filter requires a value.');
    }
    const trimmed = value.trim();
    const parsed = /^-?\d+$/.test(trimmed) ? new Date(Number(trimmed)) : new Date(trimmed);
    if (Number.isNaN(parsed.getTime())) {
        throw new Error(`Date filter received an unparseable value: ${value}`);
    }
    return parsed;
}
function parseDateIsAnyOfValues(value) {
    return parseIsAnyOfValues(value).map(parseDateValue);
}
function buildDateOperatorClause(column, operator, value) {
    switch (operator) {
        case 'equals':
        case 'is':
            return { [column]: { equals: parseDateValue(value) } };
        case 'doesNotEqual':
        case 'not':
            return { [column]: { not: { equals: parseDateValue(value) } } };
        case 'greaterThan':
            return { [column]: { gt: parseDateValue(value) } };
        case 'greaterThanOrEqual':
            return { [column]: { gte: parseDateValue(value) } };
        case 'lessThan':
            return { [column]: { lt: parseDateValue(value) } };
        case 'lessThanOrEqual':
            return { [column]: { lte: parseDateValue(value) } };
        case 'isEmpty':
            return { [column]: null };
        case 'isNotEmpty':
            return { [column]: { not: null } };
        case 'isAnyOf':
            return { [column]: { in: parseDateIsAnyOfValues(value) } };
        case 'isNoneOf':
            return { [column]: { not: { in: parseDateIsAnyOfValues(value) } } };
        default:
            return null;
    }
}
function buildBooleanOperatorClause(column, operator, value) {
    switch (operator) {
        case 'is':
        case 'equals':
            return { [column]: value === 'true' };
        case 'not':
        case 'doesNotEqual':
            return { [column]: value !== 'true' };
        default:
            return null;
    }
}
function buildRelationOperatorClause(relation, searchColumns, operator, value) {
    const buildColumnOR = (prismaOp, opValue, extraProps) => {
        return searchColumns.map((col) => ({
            [col]: { [prismaOp]: opValue, ...extraProps },
        }));
    };
    switch (operator) {
        case 'contains':
            return { [relation]: { some: { OR: buildColumnOR('contains', value, { mode: 'insensitive' }) } } };
        case 'doesNotContain':
            return { [relation]: { none: { OR: buildColumnOR('contains', value, { mode: 'insensitive' }) } } };
        case 'equals':
        case 'is':
            return { [relation]: { some: { OR: buildColumnOR('equals', value) } } };
        case 'doesNotEqual':
        case 'not':
            return { [relation]: { none: { OR: buildColumnOR('equals', value) } } };
        case 'startsWith':
            return { [relation]: { some: { OR: buildColumnOR('startsWith', value, { mode: 'insensitive' }) } } };
        case 'endsWith':
            return { [relation]: { some: { OR: buildColumnOR('endsWith', value, { mode: 'insensitive' }) } } };
        case 'isEmpty':
            return { [relation]: { none: {} } };
        case 'isNotEmpty':
            return { [relation]: { some: {} } };
        case 'isAnyOf': {
            const values = parseIsAnyOfValues(value);
            return { [relation]: { some: { OR: buildColumnOR('in', values) } } };
        }
        case 'isNoneOf': {
            const values = parseIsAnyOfValues(value);
            return { [relation]: { none: { OR: buildColumnOR('in', values) } } };
        }
        default:
            return null;
    }
}
//# sourceMappingURL=operator-handlers.js.map